import React, {
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { ApMenu, ApButtonIcon, ApMenuItem } from '@alixpartners/ui-components';
import {
	exportSpendByVendors,
	exportVendorFragmentation,
	getBreakdownSpendComparison,
	getBreakdownComparisonInfo,
	getShareOfTotalSpend,
	changeCategorisation,
} from 'utils/apiRequests';
import {
	CompaniesTopVendors,
	Title,
	HeaderTop,
	Radio,
	SectionWrapper,
	ShowMore,
	SortSelectorContainer,
	SortText,
} from './ShareOfTotalSpend.styles';
import { SpendByVendorChart } from './SpendByVendorChart';
import { SpendByVendorTable } from './SpendByVendorTable';
import { usePrevious } from '../utils';
import { InvestigateContext } from '../context';
import { convertToArray, isCustomCategory } from 'Details/utils';
import { CHART_COLORS } from 'styles/colors';
import { isEqual } from 'lodash';

import CategorisationDialog from 'Explore/CategorisationDialog';
import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import remove from 'lodash/remove';
import { handleExport } from './utils';
import {
	SPEND_TYPE,
	SPEND_TYPE_LABELS,
	makeUnknownFromEmpty,
} from '../utils';
import { SettingsContext } from 'contexts/SettingsContext';

const EXPORT_OPTIONS = [
	{
		label: 'Export chart as image',
		value: 'png',
	},
	{
		label: 'Export chart as data',
		value: 'chart_xlsx',
	},
	// TODO uncomment when done
	// {
	// 	label: 'Export table as data',
	// 	value: 'table_xlsx',
	// },
];

export const NONE_ORDER = 'None';

const createChartSeries = shareOfTotalSpendResponse => {
	return shareOfTotalSpendResponse.map((companyData, i) => ({
		color: CHART_COLORS[i],
		name: companyData.dimensionValue,
		data: companyData.data.map(
			({ percentageOfTotal, spend, vendorAmount }) => ({
				x: vendorAmount,
				y: percentageOfTotal,
				spend,
			})
		),
	}));
};

const ShareOfTotalSpendContainer = ({
	cardTitle,
	currencySymbol,
	queryParams,
	spendType,
	onDownloadStart,
	onDownloadSuccess,
	onDownloadError,
	showExplorePage,
}) => {
	const chartRef = useRef();
	const [sortOrderCompany, setSortOrderCompany] = useState(NONE_ORDER);
	const [chartSeries, setChartSeries] = useState([]);
	const [categorisationModalData, setCategorisationModalData] = useState(null);
	const [changeStatusModalData, setChangeStatusModalData] = useState(null);
	const [moveToBinModalData, setMoveToBinModalData] = useState(null);
	const [showMoreAmount, setShowMoreAmount] = useState(10);
	const [companiesData, setCompaniesData] = useState([]);
	const [comparisonInfo, setComparisonInfo] = useState([]);
	const { refreshContent } = useContext(InvestigateContext);
	const { hideDecimals } = useContext(SettingsContext);
	const sortByInRequest =
		sortOrderCompany !== NONE_ORDER ? sortOrderCompany : undefined;
	const params = {
		...queryParams,
		take: showMoreAmount,
		sortBy: sortByInRequest,
		isPurchasing: false,
	};
	const prevParams = usePrevious(params);
	let isSkipInitialSetData = false;

	useEffect(() => {
		getShareOfTotalSpend(params).then(result => {
			// TODO do not render components until state is normalized
			if (!isSkipInitialSetData) {
				setChartSeries(createChartSeries(result));
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(
		() => {
			if (prevParams && params.take !== prevParams.take) {
				const comparisonParams = {
					...params,
					skip: 0,
					take: 10,
				};
				if (queryParams.pivotingFilters && queryParams.pivotingFilters.length) {
					comparisonParams.pivotingFilters = [
						...queryParams.pivotingFilters,
					].splice(0, 3);
					if (companiesData && companiesData[0].spends) {
						comparisonParams.skip = companiesData[0].spends.length;
					}
					getBreakdownSpendComparison(comparisonParams).then(result => {
						let companies = companiesData;
						setCompaniesData(
							companies.map(company => {
								const foundCompany = result.find(
									companyResults => companyResults.name === company.name
								);
								const newSpends = foundCompany ? foundCompany.spends : [];
								return {
									...company,
									spends: [...company.spends, ...newSpends],
								};
							})
						);
					});
				}
			} else if (!isEqual(prevParams, params)) {
				// eslint-disable-next-line react-hooks/exhaustive-deps
				isSkipInitialSetData = true;
				const companyParams = {
					...params,
					skip: 0,
				};
				if (queryParams.pivotingFilters && queryParams.pivotingFilters.length) {
					companyParams.pivotingFilters = [
						...queryParams.pivotingFilters,
					].splice(0, 3);
					getBreakdownSpendComparison(companyParams).then(result =>
						setCompaniesData(result)
					);
					getBreakdownComparisonInfo(companyParams).then(result =>
						setComparisonInfo(result)
					);
				}

				getShareOfTotalSpend(params).then(result =>
					setChartSeries(createChartSeries(result))
				);
			}
		},
		[params]
	);

	const getChartInstance = () => {
		return chartRef && chartRef.current && chartRef.current.chart;
	};

	const handleChartDataExport = useCallback(
		() => {
			onDownloadStart();
			exportVendorFragmentation({
				...queryParams,
				dimensions: 'company',
			})
				.catch(onDownloadError)
				.finally(onDownloadSuccess);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[queryParams]
	);

	const handleTableDataExport = useCallback(
		() => {
			onDownloadStart();
			exportSpendByVendors({
				...queryParams,
				dimensions: 'company',
			})
				.catch(onDownloadError)
				.finally(onDownloadSuccess);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[queryParams]
	);
	const tableData = companiesData.map(companyData => {
		const companyInfo =
			comparisonInfo.find(info => info.name === companyData.name) || {};
		return {
			...companyData,
			...companyInfo,
		};
	});
	const sortedByInfo = sortByInRequest ? tableData.find(({ name }) => name === sortByInRequest) : null;
	const isSomeVendorsNotLoaded = sortByInRequest
		? sortedByInfo.vendorsCount > sortedByInfo.spends.length
		: tableData.some(({ spends, vendorsCount }) =>
			vendorsCount ? spends.length !== vendorsCount : false
	);

	const getVendorTransactionsParams = () => {
		if (!categorisationModalData) {
			return {};
		}

		return {
			filters: ['harmonizedSupplier'],
			filterValues: [categorisationModalData.vendorName],
		};
	};

	const getContextParams = () => {
		if (!categorisationModalData) {
			return {};
		}

		const companyIndexes = [];
		const filtersWithoutCompanies = queryParams.filters.filter(
			(item, index) => {
				if (item === 'company') {
					companyIndexes.push(index);

					return false;
				}

				return true;
			}
		);
		const filterValuesWithoutCompanies = [...queryParams.filterValues];

		remove(filterValuesWithoutCompanies, (_item, index) =>
			companyIndexes.includes(index)
		);

		return {
			dateFrom: queryParams.dateFrom,
			dateTo: queryParams.dateTo,
			hierarchy: queryParams.hierarchy,
			filters: [...filtersWithoutCompanies, 'harmonizedSupplier', 'company'],
			filterValues: [
				...filterValuesWithoutCompanies,
				categorisationModalData.vendorName,
				categorisationModalData.name,
			],
		};
	};

	const handleChangeCategorisation = ({
		taxonomy,
		isDirect,
		isAllTransactions,
	}) => {
		const params = isAllTransactions
			? getVendorTransactionsParams()
			: getContextParams();
		const spendType = isDirect ? SPEND_TYPE.DIRECT : SPEND_TYPE.INDIRECT;
		const data = !isDirect
			? {
					l4Code: taxonomy.aP_L4Code,
			  }
			: {
					l1Category: taxonomy.project_L1Category,
					l2Category: taxonomy.project_L2Category,
					l3Category: taxonomy.project_L3Category,
					l4Category: taxonomy.project_L4Category,
			  };

		return changeCategorisation(spendType, params, data);
	};

	const handleViewTransactionsClick = (row) => {
		const options = {};
		const hierarchy = [...queryParams.hierarchy];

		if (spendType != null) {
			options.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}
		if (hierarchy && hierarchy.length > 0) {
			options.hierarchy = hierarchy;
		}

		if (queryParams.getTailSpendOnly) {
			options.isTailSpend = true;
		}
		options.vendorName = row.vendorName;
		showExplorePage(options, row.vendorName);
	};

	const showCategorisationModal = data => {
		setCategorisationModalData(data);
	};

	const hideCategorisationModal = () => {
		setCategorisationModalData(null);
	};

	const handleChangeStatusClick = row => {
		const data = {
			dimensions: convertToArray(queryParams.dimensions),
			includeCustomCategories: isCustomCategory(queryParams.dimensions),
			isPmi: true
		};
		data.selectedDimensionKey = 'harmonizedSupplier';
		data.selectedDimensionKeyTitle = 'vendor';
		data.selectedDimensionName = row.vendorName;

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		setChangeStatusModalData(data);
	};

	const handleMoveToBinClick = row => {
		const hierarchy = [...queryParams.hierarchy];
		const data = {
			selectedDimensionKey: 'harmonizedSupplier',
			selectedDimensionName: row.vendorName,
			selectedDimensionKeyTitle: 'vendor',
			...(hierarchy.length > 0 ? { hierarchy } : {}),
		};

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		setMoveToBinModalData(data);
	};

	return (
		<SectionWrapper>
			<HeaderTop>
				<Title>{cardTitle}</Title>
				<ApMenu button={<ApButtonIcon iconName="download" />}>
					{EXPORT_OPTIONS.map(option => (
						<ApMenuItem
							key={option.value}
							onClick={() => {
								if (option.value === 'png') {
									handleExport(getChartInstance(), 'SXR-Vendor_fragmentation');
								} else if (option.value === 'chart_xlsx') {
									handleChartDataExport();
								} else if (option.value === 'table_xlsx') {
									handleTableDataExport();
								}
							}}
						>
							{option.label}
						</ApMenuItem>
					))}
				</ApMenu>
			</HeaderTop>
			<SpendByVendorChart
				chartRef={chartRef}
				currencySymbol={currencySymbol}
				series={chartSeries}
				hideDecimals={hideDecimals}
			/>
			{queryParams.pivotingFilters && queryParams.pivotingFilters.length ? (
				<Fragment>
					<SortSelectorContainer>
						<SortText>Sort all tables by order of:</SortText>
						<Radio
							value={NONE_ORDER}
							checked={sortOrderCompany === NONE_ORDER}
							onChange={e => {
								setSortOrderCompany(e.target.value);
							}}
						>
							{NONE_ORDER}
						</Radio>
						<Fragment>
							{tableData.map(({ name }) => (
								<Radio
									key={name}
									value={name}
									checked={sortOrderCompany === name}
									onChange={e => {
										setSortOrderCompany(e.target.value);
									}}
								>
									{makeUnknownFromEmpty(name)}
								</Radio>
							))}
						</Fragment>
					</SortSelectorContainer>
					<CompaniesTopVendors>
						{tableData.map(data => (
							<SpendByVendorTable
								key={data.name}
								companyData={data}
								sortOrderCompany={sortOrderCompany}
								currencySymbol={currencySymbol}
								showCategorisationModal={showCategorisationModal}
								onViewTransactionsClick={handleViewTransactionsClick}
								showChangeStatusModal={data => handleChangeStatusClick(data)}
								showMoveToBinModal={data => handleMoveToBinClick(data)}
							/>
						))}
					</CompaniesTopVendors>
					{isSomeVendorsNotLoaded && (
						<ShowMore
							onClick={() => {
								setShowMoreAmount(showMoreAmount + 10);
							}}
						>
							show 10 more items
						</ShowMore>
					)}
				</Fragment>
			) : null}

			{categorisationModalData && (
				<CategorisationDialog
					name={categorisationModalData.vendorName}
					onClose={hideCategorisationModal}
					onSave={handleChangeCategorisation}
					contextParams={getContextParams()}
					allTransactionsParams={getVendorTransactionsParams()}
				/>
			)}
			{changeStatusModalData && (
				<ChangeTypeModal
					isOpen={changeStatusModalData}
					isHarmonized
					{...changeStatusModalData}
					onClose={() => {
						setChangeStatusModalData(null);
					}}
					onSubmit={refreshContent}
					isPmi
				/>
			)}
			{moveToBinModalData && (
				<MoveToBinModal
					isOpen={moveToBinModalData}
					isHarmonized
					onClose={() => {
						setMoveToBinModalData(null);
					}}
					onSubmit={refreshContent}
					spendType={spendType}
					{...moveToBinModalData}
					isPmi
				/>
			)}
		</SectionWrapper>
	);
};

export default ShareOfTotalSpendContainer;
